// src/lib/api.js

// Always talk to same-origin /api in production (behind reverse proxy).
// In development, default directly to the local server on :4000 to avoid 404s when no proxy is configured.
const ORIGIN_API = (() => {
  if (typeof window === "undefined") return "http://localhost:4000";
  const origin = window.location?.origin || "";
  const isProd = process.env.NODE_ENV === "production";
  if (isProd) return `${origin}/api`;
  // dev: use backend directly unless explicitly overridden by REACT_APP_API_BASE
  return "http://localhost:4000";
})();

const API_BASE = (
  process.env.NODE_ENV === "production"
    ? ORIGIN_API
    : (process.env.REACT_APP_API_BASE || ORIGIN_API)
).replace(/\/$/, "");

// Simple in-memory cache (per page session)
const __cache = {
  magazinesAll: null, // { items, expiresAt }
};
const MAG_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function invalidateMagazinesCache() {
  __cache.magazinesAll = null;
}

export async function fetchAllMagazines({ page = 1, pageSize = 12, noCache = false } = {}) {
  const url = new URL(`${API_BASE}/magazines`);
  url.searchParams.set("page", String(page));
  url.searchParams.set("pageSize", String(pageSize));
  if (noCache) url.searchParams.set("t", String(Date.now()));
  const res = await fetch(url.toString(), {
    credentials: "include",
    // Hint to bypass any intermediate/browser caches when requested
    cache: noCache ? "no-store" : "default",
  });
  if (!res.ok) throw new Error(`Failed to fetch magazines (${res.status})`);
  return res.json(); // { items, total, totalPages, page, pageSize }
}

// Fetch ALL magazines across pages and cache in-memory for a short TTL.
export async function fetchAllMagazinesCached({ ttlMs = MAG_CACHE_TTL_MS } = {}) {
  const now = Date.now();
  if (__cache.magazinesAll && now < __cache.magazinesAll.expiresAt) {
    return { items: __cache.magazinesAll.items };
  }
  // Pull first page to know total pages
  const first = await fetchAllMagazines({ page: 1, pageSize: 60 });
  let all = first.items || [];
  const totalPages = first.totalPages || 1;
  for (let p = 2; p <= totalPages; p++) {
    const res = await fetchAllMagazines({ page: p, pageSize: 60 });
    if (res.items?.length) all = all.concat(res.items);
  }
  __cache.magazinesAll = { items: all, expiresAt: now + ttlMs };
  return { items: all };
}

export async function fetchMagazineBySlug(slugOrKey) {
  const res = await fetch(`${API_BASE}/magazines/${encodeURIComponent(slugOrKey)}`, { credentials: "include" });
  if (res.status === 404) throw new Error("Magazine not found");
  if (!res.ok) throw new Error(`Failed to fetch magazine (${res.status})`);
  return res.json(); // { item }
}

export async function fetchMagazineText(slug) {
  const res = await fetch(`${API_BASE}/magazines/${encodeURIComponent(slug)}/text`, { credentials: "include" });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to fetch magazine text (${res.status})`);
  return res.json(); // { textData }
}

// DEPRECATED: Legacy function for old PDF-based workflow
// New magazines should use uploadServerSideMagazine() instead
export async function createMagazine({ title, r2Key, publicUrl, publishedAt }) {
  const res = await fetch(`${API_BASE}/magazines`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ title, r2Key, publicUrl, publishedAt }),
  });
  if (!res.ok) throw new Error(`Failed to create magazine (${res.status})`);
  const json = await res.json();
  invalidateMagazinesCache();
  return json;
}

// DEPRECATED: Legacy function for old presigned URL workflow
// New magazines should use uploadServerSideMagazine() instead
export async function presignMagazineUpload({ title, publishedAt, contentType = "application/pdf", r2Key }) {
  const res = await fetch(`${API_BASE}/magazines/presign`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ title, publishedAt, contentType, r2Key }),
  });
  if (!res.ok) throw new Error(`Failed to presign upload (${res.status})`);
  return res.json(); // { ok, slug, r2Key, uploadUrl, publicUrl }
}

export async function updateMagazine(slug, { title, publishedAt, coverKey }) {
  const res = await fetch(`${API_BASE}/magazines/${encodeURIComponent(slug)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ title, publishedAt, coverKey }),
  });
  if (!res.ok) throw new Error(`Failed to update magazine (${res.status})`);
  const json = await res.json();
  invalidateMagazinesCache();
  return json;
}

export async function presignReplaceMagazine(slug, { publishedAt, contentType = "application/pdf" } = {}) {
  const res = await fetch(`${API_BASE}/magazines/${encodeURIComponent(slug)}/presign-replace`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ publishedAt, contentType }),
  });
  if (!res.ok) throw new Error(`Failed to presign replacement (${res.status})`);
  return res.json(); // { ok, slug, r2Key, uploadUrl, publicUrl }
}

export function replaceMagazine(slug, { file, title, publishedAt, onProgress }) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append('file', file);
    if (title) fd.append('title', title);
    if (publishedAt) fd.append('publishedAt', publishedAt);
    
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const uploadPercent = (e.loaded / e.total) * 100;
        onProgress(uploadPercent * 0.1, 'Uploading replacement PDF...');
      }
    });
    
    xhr.addEventListener('load', async () => {
      if (xhr.status === 202) {
        // Job accepted - start polling for progress
        try {
          const data = JSON.parse(xhr.responseText);
          const jobId = data.jobId;
          
          if (onProgress) onProgress(10, 'Processing started...');
          
          // Poll for job status
          const pollInterval = 500;
          const pollJob = async () => {
            const res = await fetch(`${API_BASE}/jobs/${jobId}`, { credentials: 'include' });
            if (!res.ok) throw new Error(`Failed to get job status (${res.status})`);
            const job = await res.json();
            
            if (onProgress && job.progress !== undefined) {
              let statusMessage = job.message || 'Processing...';
              
              // Add detailed page information if available
              if (job.detail && job.detail.page) {
                if (job.detail.total) {
                  // During upload/text extraction - we know the total
                  statusMessage = `${job.message} (${job.detail.page}/${job.detail.total})`;
                }
                // During WebP conversion - total is unknown, message already includes page number
              }
              
              onProgress(job.progress, statusMessage);
            }
            
            if (job.status === 'completed') {
              if (onProgress) onProgress(100, 'Complete!');
              invalidateMagazinesCache();
              resolve(job.result || data);
            } else if (job.status === 'failed') {
              reject(new Error(job.error || 'Processing failed'));
            } else {
              setTimeout(pollJob, pollInterval);
            }
          };
          
          pollJob();
        } catch (err) {
          reject(new Error('Failed to parse server response'));
        }
      } else if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (onProgress) onProgress(100, 'Complete!');
          invalidateMagazinesCache();
          resolve(data);
        } catch (err) {
          reject(new Error('Failed to parse server response'));
        }
      } else {
        reject(new Error(`Replace failed (${xhr.status})`));
      }
    });
    
    xhr.addEventListener('error', () => reject(new Error('Network error')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
    
    xhr.open('POST', `${API_BASE}/magazines/${encodeURIComponent(slug)}/replace`);
    xhr.withCredentials = true;
    xhr.send(fd);
  });
}

export async function replaceMagazinePage(slug, pageNumber, { file, onProgress }) {
  const fd = new FormData();
  fd.append("file", file, file.name || `page-${pageNumber}.webp`);
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const uploadPercent = (e.loaded / e.total) * 100;
        onProgress(uploadPercent, `Uploading page ${pageNumber}...`);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (onProgress) onProgress(100, 'Complete!');
          invalidateMagazinesCache();
          resolve(data);
        } catch (err) {
          reject(new Error('Failed to parse server response'));
        }
      } else {
        reject(new Error(`Replace page failed (${xhr.status})`));
      }
    });
    
    xhr.addEventListener('error', () => reject(new Error('Network error')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
    
    xhr.open('POST', `${API_BASE}/magazines/${encodeURIComponent(slug)}/replace-page/${pageNumber}`);
    xhr.withCredentials = true;
    xhr.send(fd);
  });
}

export async function deleteMagazine(slug, { deleteFile = false, deleteCover = false } = {}) {
  const url = new URL(`${API_BASE}/magazines/${encodeURIComponent(slug)}`);
  if (deleteFile) url.searchParams.set("deleteFile", "true");
  if (deleteCover) url.searchParams.set("deleteCover", "true");
  const res = await fetch(url.toString(), { method: "DELETE", credentials: "include" });
  if (!res.ok) throw new Error(`Failed to delete magazine (${res.status})`);
  const json = await res.json();
  invalidateMagazinesCache();
  return json;
}

export async function uploadToPresignedUrl(uploadUrl, file) {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/pdf" },
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
  return true;
}

// XHR-based upload with progress callback (0-100). Optional AbortSignal supported.
export function uploadToPresignedUrlWithProgress(uploadUrl, file, onProgress, signal) {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Type", file.type || "application/pdf");
      xhr.upload.onprogress = (e) => {
        if (e && e.lengthComputable && typeof onProgress === "function") {
          const pct = Math.max(0, Math.min(100, Math.round((e.loaded / e.total) * 100)));
          onProgress(pct);
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve(true);
        reject(new Error(`Upload failed (${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error("Network error during upload"));
      if (signal) {
        const onAbort = () => { try { xhr.abort(); } catch {} reject(new Error("Upload aborted")); };
        if (signal.aborted) return onAbort();
        signal.addEventListener("abort", onAbort, { once: true });
      }
      xhr.send(file);
    } catch (e) {
      reject(e);
    }
  });
}

// Server-side upload helper to bypass CORS (multipart/form-data)
export function uploadServerSideMagazine({ title, publishedAt, file, onProgress }) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    if (publishedAt) fd.append('publishedAt', publishedAt);
    
    const xhr = new XMLHttpRequest();
    
    // Track upload progress (file transfer to server)
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const uploadPercent = (e.loaded / e.total) * 100;
        onProgress(uploadPercent * 0.1, 'Uploading PDF to server...');
      }
    });
    
    xhr.addEventListener('load', async () => {
      if (xhr.status === 202) {
        // Job accepted - start polling for progress
        try {
          const data = JSON.parse(xhr.responseText);
          const jobId = data.jobId;
          
          if (onProgress) onProgress(10, 'Processing started...');
          
          // Poll for job status
          const pollInterval = 500; // Poll every 500ms
          const pollJob = async () => {
            const res = await fetch(`${API_BASE}/jobs/${jobId}`, { credentials: 'include' });
            if (!res.ok) throw new Error(`Failed to get job status (${res.status})`);
            const job = await res.json();
            
            if (onProgress && job.progress !== undefined) {
              let statusMessage = job.message || 'Processing...';
              
              // Add detailed page information if available
              if (job.detail && job.detail.page) {
                if (job.detail.total) {
                  // During upload/text extraction - we know the total
                  statusMessage = `${job.message} (${job.detail.page}/${job.detail.total})`;
                }
                // During WebP conversion - total is unknown, message already includes page number
              }
              
              onProgress(job.progress, statusMessage);
            }
            
            if (job.status === 'completed') {
              if (onProgress) onProgress(100, 'Complete!');
              invalidateMagazinesCache();
              resolve(job.result || data);
            } else if (job.status === 'failed') {
              reject(new Error(job.error || 'Processing failed'));
            } else {
              // Still processing, poll again
              setTimeout(pollJob, pollInterval);
            }
          };
          
          pollJob();
        } catch (err) {
          reject(new Error('Failed to parse server response'));
        }
      } else if (xhr.status >= 200 && xhr.status < 300) {
        // Fallback for old synchronous response
        try {
          const data = JSON.parse(xhr.responseText);
          if (onProgress) onProgress(100, 'Complete!');
          invalidateMagazinesCache();
          resolve(data);
        } catch (err) {
          reject(new Error('Failed to parse server response'));
        }
      } else {
        reject(new Error(`Upload failed (${xhr.status})`));
      }
    });
    
    xhr.addEventListener('error', () => reject(new Error('Network error')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
    
    xhr.open('POST', `${API_BASE}/magazines/upload`);
    xhr.withCredentials = true;
    xhr.send(fd);
  });
}

// Auth
export async function login({ email, password }) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error(`Login failed (${res.status})`);
  return res.json();
}

export async function logout() {
  const res = await fetch(`${API_BASE}/auth/logout`, { method: "POST", credentials: "include" });
  if (!res.ok) throw new Error(`Logout failed (${res.status})`);
  return res.json();
}

export async function me() {
  const res = await fetch(`${API_BASE}/auth/me`, { credentials: "include" });
  if (!res.ok) return { ok: false };
  return res.json();
}

// Admin: users management
export async function listUsers({ q = "", page = 1, pageSize = 20 } = {}) {
  const url = new URL(`${API_BASE}/users`);
  if (q) url.searchParams.set("q", q);
  url.searchParams.set("page", String(page));
  url.searchParams.set("pageSize", String(pageSize));
  const res = await fetch(url.toString(), { credentials: "include" });
  if (!res.ok) throw new Error(`Failed to list users (${res.status})`);
  return res.json();
}

export async function createUser({ email, password, roles = ["editor"], active = true, name = "" }) {
  const res = await fetch(`${API_BASE}/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password, roles, active, name }),
  });
  if (!res.ok) throw new Error(`Failed to create user (${res.status})`);
  return res.json();
}

export async function updateUser(id, patch) {
  const res = await fetch(`${API_BASE}/users/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Failed to update user (${res.status})`);
  return res.json();
}

export async function deleteUser(id) {
  const res = await fetch(`${API_BASE}/users/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to delete user (${res.status})`);
  return res.json();
}

export async function changeMyPassword({ currentPassword, newPassword }) {
  const res = await fetch(`${API_BASE}/auth/change-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!res.ok) throw new Error(`Failed to change password (${res.status})`);
  return res.json();
}
